'use client'

import { C, Callout, Exercise, H, Lesson, M, MB, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/calc-extra'

export default function ProjectCurveFitting() {
  return (
    <Lesson>
      <Step>
        <H>Project: find the laws of motion</H>
        <P>
          A ball is thrown straight up and its height is measured 23 times, every tenth of a second. The measurements are
          noisy. Your job is to recover the curve it follows, and from that the speed it was thrown at and the pull of gravity.
          Then you’ll use the curve to answer questions the data alone can’t.
        </P>
        <OL>
          <li>Write the model and its loss, and a general-purpose gradient calculator.</li>
          <li>Run gradient descent until the curve fits.</li>
          <li>Use calculus on the fitted curve: the peak, the landing, the total distance.</li>
        </OL>
        <Callout kind="note">
          This is machine learning in miniature: a model with parameters, a loss that measures how wrong it is, and gradient
          descent to fix it. Module 6 builds on exactly this.
        </Callout>
      </Step>

      <Step gate="fit-loss">
        <H>Part 1 · Model, loss, gradient</H>
        <P>
          Physics says a thrown object follows a parabola, so the model has three parameters:
        </P>
        <MB>{'h(t) = \\green{a} + \\green{b}\\,t + \\green{c}\\,t^2 \\qquad L(\\green{a}, \\green{b}, \\green{c}) = \\frac{1}{n}\\sum_i \\big(h(t_i) - y_i\\big)^2'}</MB>
        <P>
          For the gradient, you don’t need any derivative rules. Nudge one parameter at a time and measure how the loss changes.
          Slow, but it works for any function.
        </P>
        <Exercise
          id="fit-loss"
          title="model, loss, numericGradient"
          {...EX.fitLoss}
          hints={[
            <>
              <C>{'return p[0] + p[1] * t + p[2] * t * t'}</C>
            </>,
            <>
              In numericGradient, for each <C>i</C>: copy <C>p</C> twice with <C>p.slice()</C>, add <C>h</C> to one copy’s{' '}
              <C>[i]</C>, subtract it from the other, and return <C>{'(f(up) - f(down)) / (2 * h)'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="fit-descend">
        <H>Part 2 · Descend</H>
        <P>
          Now repeat: compute the gradient, take a small step against it. Watch the loss fall on a log scale. The fitted{' '}
          <M>{'c'}</M> should come out near <M>{'-4.9'}</M>, which is half of Earth’s gravity (9.8 m/s²). You’ll have measured
          gravity using nothing but noisy data and gradient descent.
        </P>
        <Exercise
          id="fit-descend"
          title="fit"
          {...EX.fitDescend}
          hints={[
            <>
              Each step: <C>{'history.push(loss(p))'}</C>, <C>{'const g = numericGradient(loss, p)'}</C>, then{' '}
              <C>{'p = p.map((v, i) => v - lr * g[i])'}</C>.
            </>,
          ]}
        />
        <Callout kind="try">
          Try a learning rate of 0.1, then 0.2. At some point the loss explodes instead of falling. Then try 0.01: it’s safe, but
          is it done after 4 000 steps?
        </Callout>
      </Step>

      <Step gate="fit-landing">
        <H>Part 3 · Ask the model questions</H>
        <P>
          A fitted model can answer questions the raw data can’t. When was the ball highest? When did it land (after the last
          measurement)? How far did it travel? You’ll need a derivative, a bit of algebra, bisection, and an integral.
        </P>
        <Exercise
          id="fit-landing"
          title="peakTime, landingTime, distanceTravelled"
          {...EX.fitLanding}
          hints={[
            <>
              <M>{"h'(t) = b + 2ct = 0"}</M> gives <M>{'t = -b / (2c)'}</M>: <C>{'return -p[1] / (2 * p[2])'}</C>.
            </>,
            <>
              Bisection: <C>{'let lo = peakTime(), hi = 5'}</C>; if <C>{'h(mid) > 0'}</C> then <C>lo = mid</C>, else{' '}
              <C>hi = mid</C>.
            </>,
            <>
              Distance: a midpoint sum of <C>{'Math.abs(p[1] + 2 * p[2] * t) * dt'}</C> from 0 to the landing time.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Stretch goals</H>
        <UL>
          <li>
            Replace <C>numericGradient</C> with the exact gradient, using the chain rule:{' '}
            <M>{'\\partial L/\\partial b = \\frac{2}{n}\\sum (h(t_i) - y_i)\\,t_i'}</M>, and so on. Check it agrees, and time how
            much faster it is.
          </li>
          <li>
            Fit a straight line (<M>{'a + bt'}</M>) instead. How much worse is its loss? Then fit a cubic. Is it better? Is it
            <em> right</em>? (Module 6 is all about this question.)
          </li>
          <li>Use only the first 12 measurements to fit. How well does it predict the landing time now?</li>
        </UL>
      </Step>
    </Lesson>
  )
}
